import axios from "axios";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { KButton, KIcon, KSelectMulti, KTable } from "@kyburz/kdesign-system";
import useSafeSetState from "../../../hooks/useSafeSetState";
import TableDropdown from "../../../components/TableDropdown";
import {
  deleteIncident,
  getIncidents,
} from "../../../apiCalls/admin/vehicleIncidents";
import { getAutosuggestData } from "../../../apiCalls/autosuggest";
import { setAlert } from "../../../redux/actions/actions";
import { DAMAGE_COLORS } from "../../../components/constants/incidents";
import { getUsersNew } from "../../../apiCalls/admin/users";
import { compareDatesForTable, formatDate } from "../../../utils/timeUtils";
import { RootStateInterface } from "../../../interfaces/ReduxInterface";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    height: "calc(100% - 120px)",
    [theme.breakpoints.down("lg")]: {
      height: "calc(100% - 200px)",
    },
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  filtersContainer: {
    display: "flex",
    justifyContent: "space-between",
    minWidth: 820,
    [theme.breakpoints.down("lg")]: {
      flexDirection: "column",
      minWidth: "unset",
    },
  },
  damages: {
    display: "flex",
    marginBottom: 5,
    "&:last-child": {
      margin: 0,
    },
  },
  damageLabel: {
    textTransform: "capitalize",
    minWidth: "70px",
    fontWeight: 500,
  },
  description: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const VehicleIncidents: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const history = useHistory();
  const { CancelToken } = axios;

  const [
    {
      rows,
      selectedVehicles,
      selectedUsers,
      cancelTokenSource,
      vehicles,
      users,
    },
    setState,
  ] = useSafeSetState({
    rows: [],
    vehicles: [],
    users: [],
    selectedVehicles: [],
    selectedUsers: [],
    cancelTokenSource: null,
  });

  const getIncidentsTable = () => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Loading cancelled");
    }
    const source = CancelToken.source();
    setState({ cancelTokenSource: source });

    const customerId = currentCustomer?.shortname;
    getIncidents(
      customerId,
      selectedVehicles,
      selectedUsers,
      source.token,
    ).then((data) =>
      setState({
        rows: data?.data || [],
        cancelTokenSource: null,
      }),
    );
  };

  const handleDeleteIncident = (row) => {
    const customerId = currentCustomer?.shortname;
    const incidentId = row?.row?.original?._id;
    deleteIncident(customerId, incidentId).then(() => {
      getIncidentsTable();
      dispatch(setAlert(`Incident has been deleted`, "success"));
    });
  };

  const handleEditIncident = (row) =>
    history.push(`/admin/fleet/incidents/${row?.row?.original?._id}`);

  const handleNewIncident = () => history.push(`/admin/fleet/incidents/create`);

  const getDamages = (row) => {
    const damages = row?.row?.original?.damages || [];
    return damages.map((d) => {
      const damageType = d.damage_type;
      const labelStyle = {
        color: DAMAGE_COLORS[damageType],
      };
      return (
        <div className={classes.damages}>
          <span className={classes.damageLabel} style={labelStyle}>
            {damageType}
          </span>
          <span className={classes.description}>{d.description}</span>
        </div>
      );
    });
  };

  const getCreatedBy = (row) => {
    const createdBy = row?.original?.created_by;
    return `${createdBy?.first_name || ""} ${createdBy?.last_name || ""}`;
  };

  const handleFilterChange = (name, value) =>
    setState({
      [name]: value.map((v) => v.id),
    });

  const columns = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "_id",
        defaultCanSort: true,
      },
      {
        Header: "Name",
        accessor: "vehicle_details.name",
        defaultCanSort: true,
      },
      {
        Header: "Type",
        accessor: "vehicle_type.name",
        defaultCanSort: true,
        width: 50,
      },
      {
        Header: "Damages",
        accessor: "damages",
        Cell: (row) => getDamages(row),
      },
      {
        Header: "Created by",
        accessor: "created_by",
        defaultCanSort: true,
        Cell: (row) => getCreatedBy(row?.row),
        sortType: (rowA, rowB) => {
          const createdByA = getCreatedBy(rowA);
          const createdByB = getCreatedBy(rowB);
          return createdByA > createdByB ? 1 : -1;
        },
      },
      {
        Header: "Created",
        accessor: (row) => formatDate(row.created),
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const dateA = rowA.original.created;
          const dateB = rowB.original.created;
          return compareDatesForTable(dateA, dateB);
        },
      },
      {
        Header: "Modified",
        accessor: (row) => formatDate(row.modified),
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const dateA = rowA.original.modified;
          const dateB = rowB.original.modified;
          return compareDatesForTable(dateA, dateB);
        },
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (row) => {
          return (
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "flex-end",
                marginRight: 14,
              }}
            >
              <TableDropdown
                menuItems={[
                  {
                    icon: "edit",
                    title: "Edit",
                    action: () => handleEditIncident(row),
                  },
                  {
                    icon: "trash-2",
                    title: "Delete",
                    action: () => handleDeleteIncident(row),
                  },
                ]}
              />
            </div>
          );
        },
        width: 30,
      },
    ],
    [],
  );

  useEffect(() => {
    const customerId = currentCustomer?.shortname;
    getAutosuggestData(customerId, "vehicles").then((data) => {
      const vehiclesResponse = data?.data || [];
      const vehiclesData = vehiclesResponse.map((x) => ({
        name: x?.name,
        label: x?.label,
        id: x?._id,
      }));
      setState({ vehicles: vehiclesData });
    });
    getUsersNew(customerId, 1, "", 100).then((data) => {
      const usersResponse = data?.data?.data || [];
      setState({
        users: usersResponse.map((x) => ({
          id: x._id,
          name: `${x?.first_name} ${x?.last_name}`,
        })),
      });
    });
  }, [currentCustomer]);

  useEffect(getIncidentsTable, [
    currentCustomer,
    selectedVehicles,
    selectedUsers,
  ]);

  return (
    <>
      <div className={classes.actionsContainer}>
        <div className={classes.filtersContainer}>
          <KSelectMulti
            onChange={handleFilterChange}
            name="selectedVehicles"
            label="Vehicles"
            elements={vehicles}
          />
          <KSelectMulti
            onChange={handleFilterChange}
            name="selectedUsers"
            label="Created by"
            elements={users}
          />
        </div>
        <KButton
          children="New Incident"
          icon={<KIcon icon="plus" />}
          onClick={handleNewIncident}
        />
      </div>
      <div className={classes.tableContainer}>
        <KTable data={rows} columns={columns} />
      </div>
    </>
  );
};

export default VehicleIncidents;
